import Vue from "vue";
import DOMPurify from "dompurify";
import { API_BASE_URL } from "../recovered/api-base";
import { getSessionToken } from "../recovered/token-session";

// ==========================================================================
// 会员消息 WebSocket 实时推送客户端
// 对接文档：wss://api.<域名>/ws/message?token=<登录token>
//   服务器→客户端：init / message / read / del / pong
//   客户端→服务器：{action:"read"|"del"|"ping", id?}
//   保活：客户端每 30s 主动 ping；断线指数退避重连；token 变化关旧连新
// ==========================================================================

// 与 HTTP 接口一致的登录 token（api-client 的取法保持一致）
function readToken() {
  return getSessionToken();
}

function wsUrl(token) {
  const base = String(API_BASE_URL || "").replace(/^http/i, "ws"); // https→wss / http→ws
  return `${base}/ws/message?token=${encodeURIComponent(token)}`;
}

// 全局响应式消息状态：弹窗、角标等 UI 直接读它
export const msgState = Vue.observable({
  connected: false, // WS 是否已连接
  unread: 0, // 未读数（后端下发的权威值）
  list: [], // 全部消息（init 积压 + 实时）
  queue: [], // 待弹窗展示的未读公告队列
});

let ws = null;
let curToken = "";
let pingTimer = null;
let reconnectTimer = null;
let backoff = 1000;
let manualClose = false;

// 公告仅允许必要的 HTML 标签和属性；DOMPurify 负责解析和过滤危险 URL。
// 加固点：
//  1) ALLOWED_URI_REGEXP 显式收敛 URI 协议为 https?/mailto/相对路径，
//     不依赖 DOMPurify 默认规则（默认还放行 tel/callto/xmpp 等）；
//  2) afterSanitizeAttributes 钩子在消毒后强制改写：
//     - <a>：一律 target=_blank + rel="noopener noreferrer nofollow"，
//       防 window.opener 反控与当前页钓鱼跳转；
//     - <img>/<video>/<source>：src 仅允许 https://，剥掉 http:// 追踪像素，
//       并加 referrerpolicy="no-referrer" 防止经 Referer 泄露当前 URL（含邀请参数）。
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    "p", "br", "div", "span", "b", "strong", "i", "em", "u", "s",
    "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li",
    "blockquote", "pre", "code", "a", "img", "video", "source",
    "table", "thead", "tbody", "tr", "th", "td",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "controls", "poster", "colspan", "rowspan"],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
};

let purifyHooksBound = false;

function bindPurifyHooks() {
  if (purifyHooksBound) return;
  purifyHooksBound = true;

  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.tagName === "A") {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer nofollow");
      return;
    }

    if (node.tagName === "IMG" || node.tagName === "VIDEO" || node.tagName === "SOURCE") {
      const src = node.getAttribute("src") || "";
      // 媒体源只允许 https://；相对路径与 http:// 一律剥除（防追踪像素/混合内容）
      if (src && !/^https:\/\//i.test(src)) {
        node.removeAttribute("src");
      }
      node.setAttribute("referrerpolicy", "no-referrer");
      if (node.tagName === "IMG") {
        node.setAttribute("loading", "lazy");
      }
    }
  });
}

export function sanitizeHtml(html) {
  if (!html) return "";
  bindPurifyHooks();
  return DOMPurify.sanitize(String(html), PURIFY_CONFIG);
}

function enqueue(item) {
  if (!item || item.id == null) return;
  if (Number(item.is_read)) return; // 已读不弹
  if (!msgState.queue.some((m) => m.id === item.id)) msgState.queue.push(item);
}

function handle(msg) {
  if (!msg || !msg.type) return;
  switch (msg.type) {
    case "init": {
      // 连上时的未读积压
      msgState.list = Array.isArray(msg.list) ? msg.list.slice() : [];
      msgState.unread = Number(msg.unread) || 0;
      msgState.list.forEach(enqueue);
      break;
    }
    case "message": {
      // 单条实时消息
      const item = msg.data;
      if (item && item.id != null) {
        msgState.list = [item, ...msgState.list.filter((m) => m.id !== item.id)];
        if (!Number(item.is_read)) msgState.unread += 1;
        enqueue(item);
      }
      break;
    }
    case "read": {
      // 已读回执
      msgState.unread = Number(msg.unread) || 0;
      const it = msgState.list.find((m) => m.id === msg.id);
      if (it) it.is_read = 1;
      msgState.queue = msgState.queue.filter((m) => m.id !== msg.id);
      break;
    }
    case "del": {
      // 删除回执
      msgState.unread = Number(msg.unread) || 0;
      msgState.list = msgState.list.filter((m) => m.id !== msg.id);
      msgState.queue = msgState.queue.filter((m) => m.id !== msg.id);
      break;
    }
    case "pong":
    default:
      break;
  }
}

function send(obj) {
  if (ws && ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(JSON.stringify(obj));
    } catch (e) {
      /* ignore */
    }
  }
}

// 客户端 → 服务器
export function markRead(id) {
  send({ action: "read", id });
}
export function delMessage(id) {
  send({ action: "del", id });
}
// 仅从弹窗队列移除（不回传服务器）
export function dismiss(id) {
  msgState.queue = msgState.queue.filter((m) => m.id !== id);
}

function startPing() {
  stopPing();
  pingTimer = setInterval(() => send({ action: "ping" }), 30000);
}
function stopPing() {
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function scheduleReconnect() {
  if (reconnectTimer) return;
  if (!readToken()) return; // 无 token（未登录/已登出）不重连
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    backoff = Math.min(backoff * 2, 30000); // 指数退避，上限 30s
    open(readToken());
  }, backoff);
}

function open(token) {
  if (!token) return;
  curToken = token;
  manualClose = false;
  let sock;
  try {
    sock = new WebSocket(wsUrl(token));
  } catch (e) {
    scheduleReconnect();
    return;
  }
  ws = sock;
  sock.onopen = () => {
    if (ws !== sock) return;
    msgState.connected = true;
    backoff = 1000;
    startPing();
  };
  sock.onmessage = (ev) => {
    let m;
    try {
      m = JSON.parse(ev.data);
    } catch (e) {
      return;
    }
    handle(m);
  };
  sock.onclose = () => {
    if (ws !== sock) return;
    msgState.connected = false;
    stopPing();
    if (!manualClose) scheduleReconnect();
  };
  sock.onerror = () => {
    /* 交给 onclose 处理重连 */
  };
}

// 确保已连接（App 启动 / 登录后调用）。token 变化则关旧连新，幂等。
export function ensureMessageWs() {
  const token = readToken();
  if (!token) return;
  if (
    ws &&
    curToken === token &&
    (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)
  ) {
    return;
  }
  manualClose = true;
  if (ws) {
    try {
      ws.close();
    } catch (e) {
      /* ignore */
    }
  }
  ws = null;
  backoff = 1000;
  open(token);
}

// 登出时主动断开
export function closeMessageWs() {
  manualClose = true;
  stopPing();
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (ws) {
    try {
      ws.close();
    } catch (e) {
      /* ignore */
    }
    ws = null;
  }
  curToken = "";
  msgState.connected = false;
  msgState.unread = 0;
  msgState.list = [];
  msgState.queue = [];
}
